import { Box, CircularProgress, SxProps, Theme, Typography } from '@mui/material'
import { DateTime } from 'luxon'
import moment from 'moment'
import React from 'react'
import { ChargingSessionModel } from 'src/Libs/flow/pages/charging-session-models'
import { calculateStaticSessionCostWithCurrency, formatPrice } from 'src/Libs/flow/pages/utils/calculate-session-cost/calculate-session-cost'
import { useDynamicTariff, useInstallation, usePowerBuyPrices } from 'src/Libs/flow/installation/hooks/installation-fetch-hooks'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { getCurrencyCode } from 'src/Libs/flow/utils'
import { useWallboxHistoryDataSteps } from '../pages/Dashboard/Charts/api/wallbox-history-data'
import { useDynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-provider'

interface PricingInfoProps {
  installationId: number
  session: ChargingSessionModel
  sx: SxProps<Theme>
}

export const PricingInfo: React.FC<PricingInfoProps> = ({ session, installationId, sx }) => {
  const { t } = useCustomTranslation()

  const { data: dynamicTariff } = useDynamicTariff(installationId)

  const currentCurrencyCode = getCurrencyCode(dynamicTariff?.currency ?? undefined)

  if (session.isDynamic) {
    const price = Math.max(session.dynamicTariffTotalChargePrice || 0, 0)

    return (
      <Typography variant="body2" sx={sx}>
        {formatPrice(currentCurrencyCode, price) || t('emobility.chargingSession.notAvailable')}
      </Typography>
    )
  }

  return <StaticTariffCalculation installationId={installationId} session={session} sx={sx} />
}

interface StaticTariffCalculationProps {
  installationId: number
  session: ChargingSessionModel
  sx: SxProps<Theme>
}

const StaticTariffCalculation: React.FC<StaticTariffCalculationProps> = ({ session, installationId, sx }) => {
  const { t } = useCustomTranslation()

  const { data: installation } = useInstallation(installationId)
  const firstOnline = installation?.createdAt

  const { data: dynamicTariff } = useDynamicTariff(installationId)
  const { isDynamicPowerPriceEnabled } = useDynamicPowerPricesEnabledContext()

  const start = moment(session.startAt)
  const stop = session.updatedAt ? moment(session.updatedAt) : moment()

  const currentCurrencyCode = getCurrencyCode(dynamicTariff?.currency ?? undefined)

  const firstStart = roundToPreviousQuarterHour(start)
  const firstStop = roundToNextQuarterHour(stop)

  const { data: buyingTariff, isLoading: buyingTariffLoading } = usePowerBuyPrices(installationId)

  const wallboxId = session.wallboxNumber

  const firstOnlineTimestamp = firstOnline ? DateTime.fromISO(firstOnline).toMillis() : undefined

  const {
    data: wallboxData,
    isLoading: wallboxDataIsLoading,
    isError: wallboxDataIsError,
  } = useWallboxHistoryDataSteps(
    String(session.serialno),
    wallboxId !== undefined ? String(wallboxId) : null,
    {
      from: firstStart.valueOf(),
      to: firstStop.valueOf(),
      view: 'day',
      includeFirst: false,
      includeLast: false,
      firstOnlineDate: firstOnlineTimestamp,
    },
    isDynamicPowerPriceEnabled && wallboxId !== undefined
  )

  if (wallboxDataIsLoading || buyingTariffLoading) {
    return (
      <Box sx={sx}>
        <CircularProgress size={16} />
      </Box>
    )
  }

  if (wallboxDataIsError || !buyingTariff || wallboxId === undefined) {
    return (
      <Typography variant="body2" sx={sx}>
        {t('emobility.chargingSession.notAvailable')}
      </Typography>
    )
  }

  const isPriceAvailable = Boolean(currentCurrencyCode) && wallboxData.length > 0

  if (!isPriceAvailable) {
    return (
      <Typography variant="body2" sx={sx}>
        {t('emobility.chargingSession.notAvailable')}
      </Typography>
    )
  }

  return (
    <Typography variant="body2" sx={sx}>
      {calculateStaticSessionCostWithCurrency(wallboxData, buyingTariff, currentCurrencyCode)}
    </Typography>
  )
}

const roundToPreviousQuarterHour = (momentTime: moment.Moment): moment.Moment => {
  const minutes = momentTime.minutes()
  const roundedMinutes = Math.floor(minutes / 15) * 15
  momentTime.minutes(roundedMinutes).seconds(0)
  return momentTime
}

const roundToNextQuarterHour = (momentTime: moment.Moment): moment.Moment => {
  const minutes = momentTime.minutes()
  const roundedMinutes = Math.ceil(minutes / 15) * 15

  if (roundedMinutes === 60) {
    momentTime.add(1, 'hour').minutes(0).seconds(0)
  } else {
    momentTime.minutes(roundedMinutes).seconds(0)
  }

  return momentTime
}
